// Maintains stable identities for detected animals across frames so each animal
// keeps one ID (and one heat-score panel), and debounces its lock state. This is
// the animal counterpart to Tracker (which handles human faces): the object
// detector gives a fresh, unordered set of boxes each frame, so we greedily
// match by nearest centroid to preserve identity.

import { CONFIG } from "../config";
import type { AnimalObservation } from "./animals";

/** An animal detection with a stable identity maintained across frames. */
export interface TrackedAnimal extends AnimalObservation {
  id: number;
  locked: boolean;
  /** Consecutive frames the raw lock state has held (for hysteresis). */
  lockStreak: number;
  /** Frames since this track was last matched to a detection. */
  missing: number;
  /** performance.now() when the track first became locked (0 if not locked). */
  lockedSince: number;
}

export class AnimalTracker {
  private tracks: TrackedAnimal[] = [];
  private nextId = 1;

  update(observations: AnimalObservation[], now: number): TrackedAnimal[] {
    const claimed = new Set<number>();

    // 1. Match each existing track to the nearest unclaimed observation.
    for (const track of this.tracks) {
      let best = -1;
      let bestDist: number = CONFIG.tracker.matchRadius;
      for (let i = 0; i < observations.length; i++) {
        if (claimed.has(i)) continue;
        const o = observations[i]!;
        const d = Math.hypot(o.cx - track.cx, o.cy - track.cy);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      if (best >= 0) {
        claimed.add(best);
        this.applyObservation(track, observations[best]!, now);
        track.missing = 0;
      } else {
        track.missing++;
      }
    }

    // 2. Spawn tracks for unclaimed observations.
    for (let i = 0; i < observations.length; i++) {
      if (claimed.has(i)) continue;
      this.tracks.push(this.spawn(observations[i]!, now));
    }

    // 3. Drop stale tracks.
    this.tracks = this.tracks.filter((t) => t.missing <= CONFIG.tracker.maxMissingFrames);

    return this.tracks;
  }

  /** Currently-visible animals that are locked onto the camera (making eye contact). */
  get watchers(): TrackedAnimal[] {
    return this.tracks.filter((t) => t.locked && t.missing === 0);
  }

  get visible(): TrackedAnimal[] {
    return this.tracks.filter((t) => t.missing === 0);
  }

  private applyObservation(track: TrackedAnimal, o: AnimalObservation, now: number): void {
    // Update geometry/heuristics but preserve identity + lock state.
    track.bbox = o.bbox;
    track.cx = o.cx;
    track.cy = o.cy;
    track.species = o.species;
    track.confidence = o.confidence;
    track.headFacing = o.headFacing;
    track.eyeContact = o.eyeContact;
    track.motion = o.motion;
    // Temporal EMA on the lock score before thresholding (same as faces).
    const a = CONFIG.lockOn.lockSmoothing;
    track.lockScore = a * o.lockScore + (1 - a) * track.lockScore;

    const rawLocked = track.lockScore > CONFIG.lockOn.animalLockThreshold;
    if (rawLocked === track.locked) {
      track.lockStreak = 0;
    } else {
      track.lockStreak++;
      if (track.lockStreak >= CONFIG.lockOn.lockHysteresis) {
        track.locked = rawLocked;
        track.lockStreak = 0;
        track.lockedSince = rawLocked ? now : 0;
      }
    }
  }

  private spawn(o: AnimalObservation, now: number): TrackedAnimal {
    const rawLocked = o.lockScore > CONFIG.lockOn.animalLockThreshold;
    return {
      ...o,
      id: this.nextId++,
      locked: rawLocked,
      lockStreak: 0,
      missing: 0,
      lockedSince: rawLocked ? now : 0,
    };
  }
}
